import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { ChevronRight, Trash2 } from "lucide-react";
import { useCreateCustom, useGetCustomV1Bo, usePatchCustom } from "@/api/customs.api";
import { useEffect, useState } from "react";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CustomFormData, CustomSchema } from "@/types/forms/bo/custom.form";
import { CustomValidationModal } from "@/components/backOffice/customs/customValidationModal";
import Paths from "@/constants/paths";
import { useGetTarifs } from "@/api/tarif.api";
import { CustomTarif } from "@/components/backOffice/parameters/custom/customTarif";
import { Input } from "@/components/ui/input";
import { ControlledCountryComboboxCountryCode } from "@/components/ui/customs/controlled-combobox-countryCode";
import { countries } from "@/constants/countries";
import { RegionCustomEnum } from "@/constants/enums/regionCustom.enum";
import { useAtomValue } from "jotai";
import { userAtom } from "@/stores/authStore";
import { Button } from "@/components/ui/button";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { ControlledSwitch } from "@/components/ui/customs/controlled-switch";
import { useRegionCustomOptions } from "@/hooks/useEnumOptions";
import { useTranslation } from "react-i18next";

export const CustomPage = () => {
  const { t } = useTranslation("boCustoms");
  const regionCustomOptions = useRegionCustomOptions();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const user = useAtomValue(userAtom);
  const isSuperAdmin = user?.role === "SUPERADMIN";

  const [loading, setLoading] = useState(false);
  const [validationModal, setValidationModal] = useState(false);
  const id = searchParams.get("id");

  const { data: custom } = useGetCustomV1Bo(id || "");
  const { data: tarifs } = useGetTarifs();

  const { mutateAsync: createCustom } = useCreateCustom();
  const { mutateAsync: patchCustom } = usePatchCustom();

  // Helper function to determine navigation method
  const handleNavigationBack = () => {
    // If we came from parameters page (with state), use browser back
    if (location.state?.fromParameters) {
      navigate(-1);
    } else {
      // Otherwise use hardcoded path
      navigate(Paths.bo.parametersCustoms);
    }
  };

  const { control, setValue, getValues, watch, reset, handleSubmit } = useForm<CustomFormData>({
    defaultValues: {
      id: undefined,
      bottleMaxPerShipment: undefined,
      country: undefined,
      countryCode: [],
      flagCode: undefined,
      customAdditionalRequests: [],
      customTarifs: [],
      customWineValueTaxRanges: [],
      euTarifs: false,
      label: undefined,
      maxPurchaseValue: undefined,
      recipientChargeTaxes: false,
      taxPerBottle: 0,
      taxPerLiter: 0,
      taxPerShipment: 0,
      transportPlusWinePlusInsuranceTaxPercentage: 0,
      transportPlusWineValueTaxPercentage: 0,
      usaCustomInvoice: false,
      vatPercentage: 0,
      wineValueTaxPercentage: 0,
      isCustomQuotationOnly: false,
      region: RegionCustomEnum.OTHER,
    },
    resolver: zodResolver(CustomSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    if (!custom || !tarifs) return;
    reset({
      id: custom.id,
      bottleMaxPerShipment:
        typeof custom.bottleMaxPerShipment === "string"
          ? Number(custom.bottleMaxPerShipment)
          : typeof custom.bottleMaxPerShipment === "number"
            ? custom.bottleMaxPerShipment
            : undefined,
      country: custom.country,
      flagCode: typeof custom.flagCode === "string" ? custom.flagCode : undefined,
      countryCode: custom.countryCode,
      customAdditionalRequests: custom.customAdditionalRequests,
      customTarifs: custom.customTarifs.map((el) => ({
        id: el.id,
        label: el.label,
        bottleTransportPrices: el.bottleTransportPrices,
      })),
      customWineValueTaxRanges: custom.customWineValueTaxRanges,
      euTarifs: custom.euTarifs,
      label: custom.label,
      maxPurchaseValue:
        typeof custom.maxPurchaseValue === "string"
          ? Number(custom.maxPurchaseValue)
          : typeof custom.maxPurchaseValue === "number"
            ? custom.maxPurchaseValue
            : undefined,
      recipientChargeTaxes: custom.recipientChargeTaxes,
      taxPerBottle: custom.taxPerBottle,
      taxPerLiter: custom.taxPerLiter,
      taxPerShipment: custom.taxPerShipment,
      transportPlusWinePlusInsuranceTaxPercentage:
        custom.transportPlusWinePlusInsuranceTaxPercentage,
      transportPlusWineValueTaxPercentage: custom.transportPlusWineValueTaxPercentage,
      usaCustomInvoice: custom.usaCustomInvoice,
      vatPercentage: custom.vatPercentage,
      wineValueTaxPercentage: custom.wineValueTaxPercentage,
      isCustomQuotationOnly: custom.isCustomQuotationOnly || false,
      region: custom.region as RegionCustomEnum,
    });
  }, [custom, reset, tarifs]);

  const recipientChargeTaxes = watch("recipientChargeTaxes");

  useEffect(() => {
    if (recipientChargeTaxes) {
      setValue("vatPercentage", 0);
      setValue("transportPlusWinePlusInsuranceTaxPercentage", 0);
      setValue("transportPlusWineValueTaxPercentage", 0);
      setValue("wineValueTaxPercentage", 0);
      setValue("taxPerBottle", 0);
      setValue("taxPerLiter", 0);
      setValue("taxPerShipment", 0);
      customWineValueTaxRangesFieldArray.remove();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientChargeTaxes, setValue]);

  const customWineValueTaxRangesFieldArray = useFieldArray({
    control,
    name: "customWineValueTaxRanges",
  });
  const customAdditionalRequestsFieldArray = useFieldArray({
    control,
    name: "customAdditionalRequests",
  });

  const submitForm: SubmitHandler<CustomFormData> = async (data) => {
    if (custom) {
      setValidationModal(true);
      return;
    }
    const country = countries.find((country) => country.countryCode === data.flagCode);
    const ret = await createCustom({
      ...data,
      countryCode: data.flagCode ? [data.flagCode] : [],
      country: country?.label_fr || "",
      bottleMaxPerShipment: data.bottleMaxPerShipment || null,
      maxPurchaseValue: data.maxPurchaseValue || null,
    });
    setLoading(false);
    if (ret.status === 200 || ret.status === 201) {
      handleNavigationBack();
    }
  };

  const cancelEditCustom = () => {
    setValidationModal(false);
  };

  const confirmEditCustom = async () => {
    setLoading(true);
    const { id, ...data } = getValues();
    const country = countries.find((country) => country.countryCode === data.flagCode);
    const ret = await patchCustom({
      id: id || "",
      payload: {
        ...data,
        flagCode: data.flagCode || undefined,
        country: country?.label_fr || "",
        countryCode: data.flagCode ? [data.flagCode] : [],
        bottleMaxPerShipment: data.bottleMaxPerShipment || null,
        maxPurchaseValue: data.maxPurchaseValue || null,
        customAdditionalRequests: data.customAdditionalRequests.map((el) => ({
          description: el.description,
          title: el.title,
        })),
        customTarifs: data.customTarifs,
        customWineValueTaxRanges: data.customWineValueTaxRanges.map((el) => ({
          from: el.from,
          to: el.to,
          value: el.value,
        })),
      },
    });
    setLoading(false);
    if (ret.status === 200 || ret.status === 201) {
      handleNavigationBack();
    }
  };

  if (searchParams.get("id") && !custom) {
    return <StyledSpinner />;
  }

  return (
    <div className="flex h-fit w-full flex-col gap-8 p-8">
      <div className="flex w-fit flex-row items-center gap-2">
        <Button
          variant="ghost"
          className="px-0 text-gray-500 hover:bg-transparent"
          onClick={handleNavigationBack}
        >
          {t("customPage.breadcrumbParameters")}
        </Button>
        <ChevronRight className="size-4" />
        <Button
          variant="ghost"
          className="px-0 text-gray-500 hover:bg-transparent"
          onClick={handleNavigationBack}
        >
          {t("customPage.breadcrumbCustoms")}
        </Button>
        <ChevronRight className="size-4" />
        <span className="text-brand700 text-sm font-medium focus:outline-hidden">
          {custom?.label || t("customPage.createTitle")}
        </span>
      </div>
      <h1 className="font-jakarta-sans text-3xl font-semibold">
        {custom?.label || t("customPage.createTitle")}
      </h1>
      <div className="flex w-full justify-center">
        <form
          className="flex w-full max-w-[820px] flex-col gap-8"
          onSubmit={handleSubmit(submitForm)}
        >
          <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-8 shadow-xs">
            <span className="font-medium text-black">{t("customPage.generalSection")}</span>
            <div className="flex flex-row gap-2">
              <ControlledUnifiedInput
                name="label"
                control={control}
                wrapperClassName="flex-1"
                label={t("customPage.nameLabel")}
                disabled={!isSuperAdmin}
              />
            </div>
            <ControlledCountryComboboxCountryCode
              name="flagCode"
              control={control}
              placeholder={t("customPage.countryPlaceholder")}
              wrapperClassName="pb-2"
              onChange={() => {}}
              disabled={!isSuperAdmin}
            />
            <ControlledSelectV2
              label={t("customPage.regionLabel")}
              name="region"
              control={control}
              options={regionCustomOptions}
              isDisabled={!isSuperAdmin}
            />
            <ControlledSwitch
              label={t("customPage.customQuotationOnlyLabel")}
              name="isCustomQuotationOnly"
              control={control}
              disabled={!isSuperAdmin}
              labelPosition="left"
              className="flex-1"
            />
            <ControlledSwitch
              label={t("customPage.euTarifsLabel")}
              name="euTarifs"
              control={control}
              disabled={!isSuperAdmin}
              labelPosition="left"
              className="flex-1"
            />
            <ControlledSwitch
              label={t("customPage.usaCustomInvoiceLabel")}
              name="usaCustomInvoice"
              control={control}
              disabled={!isSuperAdmin}
              labelPosition="left"
              className="flex-1"
            />
            <ControlledSwitch
              label={t("customPage.recipientChargeTaxesLabel")}
              name="recipientChargeTaxes"
              control={control}
              disabled={!isSuperAdmin}
              labelPosition="left"
              className="flex-1"
            />
            <ControlledUnifiedInput
              control={control}
              name="bottleMaxPerShipment"
              inputType="integer"
              label={t("customPage.bottleMaxPerShipmentLabel")}
              tooltip={t("customPage.noLimitHelp")}
              disabled={!isSuperAdmin}
            />
            <ControlledUnifiedInput
              control={control}
              name="maxPurchaseValue"
              inputType="integer"
              label={t("customPage.maxPurchaseValueLabel")}
              tooltip={t("customPage.noLimitHelp")}
              disabled={!isSuperAdmin}
            />
          </div>
          <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-8 shadow-xs">
            <span className="font-medium text-black">{t("customPage.taxesSection")}</span>
            <ControlledUnifiedInput
              control={control}
              name="vatPercentage"
              inputType="float"
              label={t("customPage.vatPercentageLabel")}
              tooltip={t("customPage.notApplicableHelp")}
              disabled={recipientChargeTaxes || !isSuperAdmin}
            />
            <ControlledUnifiedInput
              control={control}
              name="taxPerShipment"
              inputType="float"
              label={t("customPage.taxPerShipmentLabel")}
              tooltip={t("customPage.notApplicableHelp")}
              disabled={recipientChargeTaxes || !isSuperAdmin}
            />
            <ControlledUnifiedInput
              control={control}
              name="wineValueTaxPercentage"
              inputType="float"
              label={t("customPage.wineValueTaxPercentageLabel")}
              tooltip={t("customPage.notApplicableHelp")}
              disabled={recipientChargeTaxes || !isSuperAdmin}
            />
            <ControlledUnifiedInput
              control={control}
              name="taxPerBottle"
              inputType="float"
              label={t("customPage.taxPerBottleLabel")}
              tooltip={t("customPage.notApplicableHelp")}
              disabled={recipientChargeTaxes || !isSuperAdmin}
            />
            <ControlledUnifiedInput
              control={control}
              name="taxPerLiter"
              inputType="float"
              label={t("customPage.taxPerLiterLabel")}
              tooltip={t("customPage.notApplicableHelp")}
              disabled={recipientChargeTaxes || !isSuperAdmin}
            />
            <ControlledUnifiedInput
              control={control}
              name="transportPlusWineValueTaxPercentage"
              inputType="float"
              label={t("customPage.transportPlusWineValueTaxPercentageLabel")}
              tooltip={t("customPage.notApplicableHelp")}
              disabled={recipientChargeTaxes || !isSuperAdmin}
            />
            <ControlledUnifiedInput
              control={control}
              name="transportPlusWinePlusInsuranceTaxPercentage"
              inputType="float"
              label={t("customPage.transportPlusWinePlusInsuranceTaxPercentageLabel")}
              tooltip={t("customPage.notApplicableHelp")}
              disabled={recipientChargeTaxes || !isSuperAdmin}
            />
          </div>
          <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-8 shadow-xs">
            <span className="font-medium text-black">{t("customPage.informationsSection")}</span>
            {customAdditionalRequestsFieldArray.fields.map((_field, index) => (
              <div key={index} className="flex w-full flex-col items-center gap-2">
                <ControlledUnifiedInput
                  control={control}
                  name={`customAdditionalRequests.${index}.title`}
                  inputType="text"
                  label={t("customPage.additionalRequestTitleLabel")}
                  wrapperClassName="w-full"
                  disabled={!isSuperAdmin}
                />
                <ControlledUnifiedInput
                  control={control}
                  name={`customAdditionalRequests.${index}.description`}
                  inputType="text"
                  label={t("customPage.additionalRequestDescriptionLabel")}
                  wrapperClassName="w-full"
                  disabled={!isSuperAdmin}
                />
                {isSuperAdmin && (
                  <Button
                    type="button"
                    variant="tertiary"
                    onClick={() => {
                      customAdditionalRequestsFieldArray.remove(index);
                    }}
                  >
                    {t("customPage.removeAdditionalRequest")}
                  </Button>
                )}
              </div>
            ))}
            {isSuperAdmin && (
              <Button
                type="button"
                variant="tertiary"
                className="w-fit"
                onClick={() => {
                  customAdditionalRequestsFieldArray.append({
                    title: "",
                    description: "",
                  });
                }}
              >
                {t("customPage.addAdditionalRequest")}
              </Button>
            )}
          </div>
          <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-8 shadow-xs">
            <span className="font-medium text-black">
              {t("customPage.wineValueTaxRangesSection")}
            </span>
            {customWineValueTaxRangesFieldArray.fields.map((_field, index) => (
              <div key={index} className="flex flex-row items-center justify-between gap-2">
                <ControlledUnifiedInput
                  control={control}
                  name={`customWineValueTaxRanges.${index}.from`}
                  inputType="integer"
                  label={t("customPage.rangeFromLabel")}
                  wrapperClassName="flex-1"
                  disabled={!isSuperAdmin}
                />
                <ControlledUnifiedInput
                  control={control}
                  name={`customWineValueTaxRanges.${index}.to`}
                  inputType="integer"
                  label={t("customPage.rangeToLabel")}
                  wrapperClassName="flex-1"
                  disabled={!isSuperAdmin}
                />
                <ControlledUnifiedInput
                  control={control}
                  name={`customWineValueTaxRanges.${index}.value`}
                  inputType="float"
                  label={t("customPage.rangeValueLabel")}
                  wrapperClassName="flex-1"
                  disabled={!isSuperAdmin}
                />
                {isSuperAdmin && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      customWineValueTaxRangesFieldArray.remove(index);
                    }}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </div>
            ))}
            {isSuperAdmin && (
              <Button
                type="button"
                disabled={recipientChargeTaxes}
                variant="tertiary"
                className="w-fit"
                onClick={() => {
                  customWineValueTaxRangesFieldArray.append({
                    from: 0,
                    to: 0,
                    value: 0,
                  });
                }}
              >
                {t("customPage.addWineValueTaxRange")}
              </Button>
            )}
          </div>
          <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-8 shadow-xs">
            <span className="font-medium text-black">{t("customPage.bottleTarifsSection")}</span>
            <div className="flex flex-row gap-6">
              <CustomTarif
                id={id || ""}
                custom={custom}
                tarifs={tarifs}
                control={control}
                watch={watch}
                disabled={!isSuperAdmin}
              />
              <div className="flex flex-col gap-4">
                <div className="flex flex-row items-center gap-2 pt-3">
                  <p>Express</p>
                </div>
                <div className="flex flex-col justify-center gap-1">
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <p>{t("customPage.bottleCountHeader")}</p>
                    <p>{t("customPage.bottleValueHeader")}</p>
                  </div>
                  {custom?.customTarifs
                    .find((tarif) => tarif.label === "Express")
                    ?.bottleTransportPrices.map((price, index) => (
                      <div key={index} className="flex flex-row items-center justify-between gap-2">
                        <Input value={price.quantity} disabled />
                        <Input value={price.amount} disabled />
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
          {isSuperAdmin && (
            <Button disabled={loading} variant="default">
              {t("customPage.submit")}
            </Button>
          )}
        </form>
      </div>
      <CustomValidationModal
        open={validationModal}
        onClose={cancelEditCustom}
        onConfirm={confirmEditCustom}
        loading={loading}
      />
    </div>
  );
};
